import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ensurePushSubscription } from "@/lib/push";
import { fetchNewBadges, markBadgesSeen } from "@/lib/badges";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import NotificationBell from "@/components/NotificationBell";
import BadgeCelebration from "@/components/BadgeCelebration";
import { Calculator, BookOpen, LogOut, Bell, MessageSquare, Sparkles, ArrowLeft, User as UserIcon, Target } from "lucide-react";
import MobileNav from "@/components/MobileNav";

export default function StudentDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [me, setMe] = useState(null);
    const [updates, setUpdates] = useState({});
    const [newBadges, setNewBadges] = useState([]);

    useEffect(() => {
        api.get("/student/me").then((r) => setMe(r.data)).catch(() => {});
        api.get("/student/updates").then((r) => setUpdates(r.data)).catch(() => {});
        ensurePushSubscription();
        if (user?.id) fetchNewBadges(user.id).then(setNewBadges);
    }, [user?.id]);

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <BadgeCelebration
                badgeIds={newBadges}
                onDismiss={(ids) => { markBadgesSeen(user?.id, ids); setNewBadges([]); }}
            />
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                    <BrandLogo size={40} />
                    <MobileNav
                        title="القائمة"
                        inlineExtras={<NotificationBell onMessages={() => nav("/messages")} />}
                        items={[
                            { label: "حسابي", icon: UserIcon, onClick: () => nav("/profile"), testid: "btn-profile" },
                            { label: "الرسائل", icon: MessageSquare, onClick: () => nav("/messages"), testid: "btn-open-messages" },
                            { label: "صفحة أدائي", icon: Target, onClick: () => nav("/student/performance"), testid: "btn-open-performance" },
                            { label: "تسجيل الخروج", icon: LogOut, onClick: async () => { await logout(); nav("/login"); }, destructive: true, testid: "btn-logout" },
                        ]}
                    />
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
                {/* Hero */}
                <div className="bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] text-white rounded-2xl p-8 relative overflow-hidden">
                    <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-[#C8102E]/10 blur-3xl" />
                    <div className="relative">
                        <div className="text-xs text-white/60 uppercase tracking-wider mb-2">مرحباً بك</div>
                        <h1 className="text-3xl md:text-4xl font-bold">{user?.full_name}</h1>
                        <p className="text-white/70 mt-2 text-sm">{me?.class?.name} — رحلتك نحو التميز في اختبار القدرات تبدأ من هنا</p>
                    </div>
                </div>

                {/* Two big section cards */}
                <div>
                    <h2 className="text-lg font-semibold text-[#0F172A] mb-4">الأقسام</h2>
                    <div className="grid md:grid-cols-2 gap-5">
                        <button
                            onClick={() => nav("/student/section/quantitative")}
                            data-testid="section-card-quantitative"
                            className="group bg-white text-right rounded-2xl border border-slate-200 p-8 hover:border-[#1B2A4E] hover:shadow-xl hover:-translate-y-1 transition-all"
                        >
                            <div className="w-14 h-14 rounded-xl bg-[#1B2A4E] text-white flex items-center justify-center mb-4">
                                <Calculator className="w-7 h-7" />
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-2xl font-bold text-[#0F172A]">القدرات الكمية</h3>
                                <ArrowLeft className="w-5 h-5 text-slate-300 group-hover:text-[#1B2A4E] group-hover:-translate-x-1 transition-all" />
                            </div>
                            <p className="text-sm text-slate-500">مسائل الحساب والجبر والهندسة والتحليل الكمي</p>
                            {updates.latest_quant && (
                                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                                    <span className="text-[#1B2A4E] font-semibold">آخر موضوع:</span> {updates.latest_quant.title}
                                </div>
                            )}
                        </button>

                        <button
                            onClick={() => nav("/student/section/verbal")}
                            data-testid="section-card-verbal"
                            className="group bg-white text-right rounded-2xl border border-slate-200 p-8 hover:border-[#C8102E] hover:shadow-xl hover:-translate-y-1 transition-all"
                        >
                            <div className="w-14 h-14 rounded-xl bg-[#C8102E] text-white flex items-center justify-center mb-4">
                                <BookOpen className="w-7 h-7" />
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-2xl font-bold text-[#0F172A]">القدرات اللفظية</h3>
                                <ArrowLeft className="w-5 h-5 text-slate-300 group-hover:text-[#C8102E] group-hover:-translate-x-1 transition-all" />
                            </div>
                            <p className="text-sm text-slate-500">المفردات والتناظر اللفظي وإكمال الجُمل والاستيعاب</p>
                            {updates.latest_verbal && (
                                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                                    <span className="text-[#C8102E] font-semibold">آخر موضوع:</span> {updates.latest_verbal.title}
                                </div>
                            )}
                        </button>
                    </div>
                </div>

                {/* Latest updates */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[#C8102E]" />
                            <h2 className="text-lg font-semibold text-[#0F172A]">آخر المستجدات</h2>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => nav("/student/performance")} data-testid="btn-performance">
                            <Target className="w-4 h-4 ml-1" /> صفحة أدائي
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button onClick={() => nav("/student/section/quantitative")} className="bg-white rounded-xl border border-slate-200 p-4 text-right hover:shadow-md transition-all">
                            <div className="text-xs text-slate-500 mb-1">أحدث كمي</div>
                            <div className="text-sm font-semibold text-[#0F172A] truncate">{updates.latest_quant?.title || "—"}</div>
                        </button>
                        <button onClick={() => nav("/student/section/verbal")} className="bg-white rounded-xl border border-slate-200 p-4 text-right hover:shadow-md transition-all">
                            <div className="text-xs text-slate-500 mb-1">أحدث لفظي</div>
                            <div className="text-sm font-semibold text-[#0F172A] truncate">{updates.latest_verbal?.title || "—"}</div>
                        </button>
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Bell className="w-3 h-3" /> إشعارات جديدة</div>
                            <div className="text-2xl font-bold font-mono-num text-[#C8102E]">{updates.unread_notifications ?? 0}</div>
                        </div>
                        <button onClick={() => nav("/messages")} className="bg-white rounded-xl border border-slate-200 p-4 text-right hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><MessageSquare className="w-3 h-3" /> رسائل جديدة</div>
                            <div className="text-2xl font-bold font-mono-num text-[#1B2A4E]">{updates.unread_messages ?? 0}</div>
                        </button>
                    </div>
                    {updates.unread_topics > 0 && (
                        <div className="mt-3 bg-[#FFF1F2] border border-[#FECACA] rounded-xl p-3 text-sm text-[#991B1B] flex items-center gap-2">
                            <Badge className="bg-[#C8102E] text-white hover:bg-[#C8102E]">{updates.unread_topics}</Badge>
                            <span>موضوعات لم تفتحيها بعد</span>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
